import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Input, DateField } from '@structural-climbing/ui'
import type { CompetitionSession } from './types'

interface Props {
  competitionId: string
  /** The competition window; a group has to sit inside it. */
  startsAt: string
  endsAt: string
  sessions: CompetitionSession[]
  /** The competition is over, so its shape is the result and cannot change. */
  locked: boolean
  onRefresh: () => void
}

interface Draft {
  name: string
  starts_at: string
  ends_at: string
  capacity: string
}

const EMPTY: Draft = { name: '', starts_at: '', ends_at: '', capacity: '' }

/** Trims a stored wall-clock value to the minute-precision value a DateField takes. */
function bound(value: string): string {
  return value.slice(0, 16)
}

export function SessionsTab({
  competitionId,
  startsAt,
  endsAt,
  sessions,
  locked,
  onRefresh,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [draft, setDraft] = useState<Draft>({
    ...EMPTY,
    // Opening on the competition's own window makes the common case one tap.
    starts_at: bound(startsAt),
    ends_at: bound(endsAt),
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function resetForm() {
    setDraft({ ...EMPTY, starts_at: bound(startsAt), ends_at: bound(endsAt) })
    setEditingId(null)
    setError(null)
  }

  function startEditing(session: CompetitionSession) {
    setDraft({
      name: session.name,
      starts_at: bound(session.starts_at),
      ends_at: bound(session.ends_at),
      capacity: session.capacity === null ? '' : String(session.capacity),
    })
    setEditingId(session.id)
    setError(null)
  }

  async function saveSession() {
    if (!draft.name.trim() || !draft.starts_at || !draft.ends_at) {
      setError(t('competitions.errorGroupFields'))
      return
    }
    if (draft.ends_at <= draft.starts_at) {
      setError(t('competitions.errorEndBeforeStart'))
      return
    }
    if (draft.starts_at < bound(startsAt) || draft.ends_at > bound(endsAt)) {
      setError(t('competitions.errorGroupOutsideWindow'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fields = {
        name: draft.name.trim(),
        starts_at: draft.starts_at,
        ends_at: draft.ends_at,
        capacity: draft.capacity ? Number(draft.capacity) : null,
      }
      // Editing keeps the group's place in the list, so sort_order is left alone.
      const res = editingId
        ? await fetch(`${apiUrl}/competition-sessions/${editingId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(fields),
          })
        : await fetch(`${apiUrl}/competitions/${competitionId}/sessions`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...fields, sort_order: sessions.length }),
          })
      if (!res.ok) {
        setError(t('competitions.errorSaveFailed'))
        return
      }
      resetForm()
      onRefresh()
    } finally {
      setSaving(false)
    }
  }

  async function removeSession(id: string) {
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competition-sessions/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (id === editingId) resetForm()
    onRefresh()
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-foreground/60">
        {t('competitions.groupsHint')}{' '}
        {t('competitions.groupsWindowHint', {
          from: startsAt.slice(0, 16).replace('T', ' '),
          to: endsAt.slice(0, 16).replace('T', ' '),
        })}
      </p>

      {sessions.length === 0 ? (
        <Card className="shadow-sm">
          <CardBody className="py-6 text-center text-foreground/60">
            {/* This is a real configuration, not an empty state to fix. */}
            {t('competitions.groupsEmptyMeansAllDay')}
          </CardBody>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {sessions.map((session) => (
            <li
              key={session.id}
              className={`flex items-center justify-between gap-4 rounded-lg border p-3 ${
                session.id === editingId ? 'border-primary' : 'border-default-200'
              }`}
            >
              <div>
                <p className="font-medium">{session.name}</p>
                <p className="text-sm text-foreground/60">
                  {session.starts_at.slice(11, 16)}–{session.ends_at.slice(11, 16)} ·{' '}
                  {session.capacity === null
                    ? t('competitions.groupUncapped')
                    : t('competitions.groupCapacity', { count: session.capacity })}
                </p>
              </div>
              {!locked && (
                <div className="flex gap-1">
                  <Button size="sm" variant="light" onPress={() => startEditing(session)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => void removeSession(session.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {locked ? (
        <p className="rounded-lg bg-content2 p-3 text-sm text-foreground/60">
          {t('competitions.endedLockNote')}
        </p>
      ) : (
        <Card className="shadow-sm">
          <CardBody className="gap-3">
            <h3 className="font-semibold">
              {t(editingId ? 'competitions.editGroup' : 'competitions.addGroup')}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('competitions.groupName')}
                value={draft.name}
                onValueChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              />
              <Input
                type="number"
                min={1}
                label={t('competitions.groupCapacityLabel')}
                description={t('competitions.groupCapacityHint')}
                value={draft.capacity}
                onValueChange={(v) => setDraft((d) => ({ ...d, capacity: v }))}
              />
              <DateField
                withTime
                label={t('competitions.fieldStartsAt')}
                min={bound(startsAt)}
                max={bound(endsAt)}
                value={draft.starts_at}
                onValueChange={(v) => setDraft((d) => ({ ...d, starts_at: v }))}
              />
              <DateField
                withTime
                label={t('competitions.fieldEndsAt')}
                min={bound(startsAt)}
                max={bound(endsAt)}
                value={draft.ends_at}
                onValueChange={(v) => setDraft((d) => ({ ...d, ends_at: v }))}
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2">
              <Button
                color="primary"
                size="sm"
                isLoading={saving}
                onPress={() => void saveSession()}
              >
                {t(editingId ? 'common.save' : 'competitions.addGroup')}
              </Button>
              {editingId && (
                <Button size="sm" variant="light" onPress={resetForm}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
